import { useState } from "react";
import { useHistory } from "react-router-dom";
import { Col, Form, Input, Row, Spin, notification } from "antd";

import { withProcedureWraper } from "../../../..";
import {
  AlternateApproverSelect,
  ApproverSelect,
  Box,
  ButtonList,
  DepartmentSelect,
  Icon,
  UnitSelect,
} from "../../../../../../components";

import {
  Page,
  RegExp,
  RequestStatus,
  RequestType,
  SystemCode,
} from "../../../../../../constants";
import { useBCCreate, useBCSteps } from "../../hooks";
import { StringHelper } from "../../../../../../helper";
import { IBusinessCard } from "../../models/IBusinessCard";
import { useQueryClient } from "@tanstack/react-query";
import { Routes } from "../../../../../../AppRoutes";
import { getPageURL } from "../../../../../../services/serviceHelper";

type IBusinessCardForm = Omit<IBusinessCard, "id" | "eApprovalProcess">;

const BCCreate = withProcedureWraper(() => {
  const queryClient = useQueryClient();
  const history = useHistory();
  const [eApprovedBy, setEApprovedBy] = useState("");

  const { mutate, isLoading } = useBCCreate();
  const { getAvailableStep, sendMailAP } = useBCSteps();

  const handleFinish = (values: IBusinessCardForm) => {
    const { empty, initialize, eAssignee } = getAvailableStep(SystemCode.BC);
    const approvedByName =
      StringHelper.verticalBarSplit<string[]>(eApprovedBy)[1];
    const approvedByEmail =
      StringHelper.verticalBarSplit<string[]>(eApprovedBy)[0];
    const eApprovalProcess = [
      [initialize],
      [
        {
          ...empty,
          title: "Thủ trưởng CQ/ĐV",
          name: approvedByName,
          email: approvedByEmail,
        },
      ],
      [{ ...eAssignee, title: "Bộ phận hành chính VPCT" }],
    ];
    const body = {
      ...values,
      Title: "Đề nghị in danh thiếp",
      eRequestType: RequestType.provide,
      eRequestStatus: RequestStatus.submitted,
      eApprovalProcess,
    };
    mutate(body, {
      onSuccess(response: any) {
        queryClient.invalidateQueries({ queryKey: ["MTP-requests"] });
        sendMailAP(response.fields.eApprovalProcess, {
          toPath: `${getPageURL(Page.businessCard)}/${response.id}`,
          type: RequestStatus.approved,
        });
        history.push(`${Routes.businessCard.path}/${response.id}`);
        notification.success({
          message: "Thành công!",
          description: `Tạo yêu cầu thành công.`,
        });
      },
      onError() {
        notification.error({
          message: "Thất bại",
          description: `Tạo yêu cầu thất bại.`,
        });
      },
    });
  };

  const handleValuesChange = (changedValue: any = {}) => {
    const { eApprovedBy, eAlternateApprover } = changedValue;
    if (eApprovedBy || eAlternateApprover) {
      setEApprovedBy(eApprovedBy || eAlternateApprover);
    }
  };

  return (
    <Spin spinning={isLoading}>
      <Form
        size="large"
        layout="vertical"
        onValuesChange={handleValuesChange}
        onFinish={handleFinish}
      >
        <div className="pd15">
          <div className="PageHead flex-center">
            <div className="title" style={{ margin: 0 }}>
              <div className="btnBack" onClick={() => history.goBack()}>
                <Icon.ArrowLeft />
              </div>
              Tạo phiếu đề nghị in danh thiếp
            </div>
          </div>
          <Box title="Thông tin">
            <Row gutter={[32, 0]}>
              <Col span={8}>
                <Form.Item
                  label="Họ tên"
                  name="eName"
                  rules={[
                    { required: true, message: "Vui lòng nhập trường này!" },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Cơ quan/Đơn vị"
                  name="eDepartment"
                  rules={[
                    { required: true, message: "Vui lòng chọn trường này!" },
                  ]}
                >
                  <DepartmentSelect />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Đơn vị"
                  name="eUnit"
                  rules={[
                    { required: true, message: "Vui lòng chọn trường này!" },
                  ]}
                >
                  <UnitSelect />
                </Form.Item>
              </Col>
            </Row>
          </Box>
          <Box title="Thông tin in danh thiếp">
            <Row gutter={[32, 0]}>
              <Col span={8}>
                <Form.Item
                  label="Họ và tên"
                  name="eCardName"
                  rules={[
                    { required: true, message: "Vui lòng nhập trường này!" },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Chức danh"
                  name="eCardTitle"
                  rules={[
                    { required: true, message: "Vui lòng nhập trường này!" },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Email"
                  name="eCardEmail"
                  rules={[
                    { required: true, message: "Vui lòng nhập trường này!" },
                    {
                      pattern: RegExp.viagsEmail,
                      message: "E-mail phải kết thúc bằng @viags.vn!",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Địa chỉ"
                  name="eCardAddress"
                  rules={[
                    { required: true, message: "Vui lòng nhập trường này!" },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Địa chỉ website"
                  name="eCardSite"
                  rules={[
                    { required: true, message: "Vui lòng nhập trường này!" },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Số điện thoại"
                  name="eCardPhone"
                  rules={[
                    { required: true, message: "Vui lòng nhập trường này!" },
                    {
                      pattern: RegExp.vnPhoneNumber,
                      message: "Định dạng số điện thoại không đúng!",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="Ghi chú" name="eDescription">
                  <Input.TextArea />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  required
                  label="Người duyệt"
                  name="eApprovedBy"
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (value || getFieldValue("eAlternateApprover")) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("Vui lòng chọn trường này!")
                        );
                      },
                    }),
                  ]}
                >
                  <ApproverSelect placeholder="Thủ trưởng CQ/ĐV" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Người duyệt thay thế"
                  name="eAlternateApprover"
                >
                  <AlternateApproverSelect />
                </Form.Item>
              </Col>
            </Row>
          </Box>
          <div className="flex-end">
            <ButtonList
              items={[
                {
                  buttonType: "danger",
                  label: "Huỷ",
                  onClick: () => history.goBack(),
                },
                {
                  buttonType: "warning",
                  label: "Xác nhận",
                  type: "submit",
                },
              ]}
            />
          </div>
        </div>
      </Form>
    </Spin>
  );
});

export default BCCreate;
